/**
 * Health. What is up, what is waiting, what failed, and what we do not measure yet.
 *
 * There is no last-backup timestamp on this page and there deliberately is not one: the process
 * runs as `h1` and cannot read `/var/backups/h1`, so the Backups card names the systemd units that
 * take and prove the dumps instead. A console that could read the backups could also be used to
 * find them. (`HealthSummary.lastBackupAgeMs` is on the wire and always null; nothing reads it.)
 *
 * Author: Danish Khan
 */
import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import type { ReactNode } from 'react';
import { api } from '../lib/api.ts';
import { formatDuration } from '../lib/format.ts';
import { ErrorState } from '../components/ErrorState.tsx';
import { MetricTile } from '../components/MetricTile.tsx';
import { Pill } from '../components/Pill.tsx';

const STATE_TONE = { ok: 'ok', warn: 'warn', down: 'danger', unknown: 'neutral' } as const;
const HEALTH_METRICS = ['first_reply_p50', 'first_reply_p90', 'guard_fire_rate', 'handoff_claim_p90', 'outcome_recorded_rate', 'messages_out_today'];
const LOWER_IS_BETTER = new Set(['first_reply_p50', 'first_reply_p90', 'guard_fire_rate', 'handoff_claim_p90']);

export function HealthPage(): ReactNode {
  // The one place a timer survives the move to the event stream, and deliberately: nothing NOTIFYs
  // when a Meta token ages past 51 days or a circuit breaker trips. Those are sampled, not
  // published, so this page asks. Everything the stream does cover — messages, deliveries,
  // handoffs — invalidates this query too (src/lib/events.tsx).
  const health = useQuery({ queryKey: ['health'], queryFn: () => api.health(), refetchInterval: 30_000 });
  const metrics = useQuery({ queryKey: ['metrics', HEALTH_METRICS], queryFn: () => api.metrics(HEALTH_METRICS) });

  if (health.isError) {
    return <ErrorState error={health.error} context="load system health" onRetry={() => void health.refetch()} />;
  }
  const data = health.data;
  const loading = health.isLoading;

  return (
    <div className="h1-scroll h-full overflow-y-auto px-5 py-5">
      <div className="mx-auto flex max-w-6xl flex-col gap-6">
        <section>
          <h2 className="mb-2.5 text-sm font-semibold text-fg">Services</h2>
          {loading && <p className="text-sm text-fg-muted">Asking each one…</p>}
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {(data?.services ?? []).map((s) => (
              <div key={s.key} className="h1-card flex items-start gap-3 p-3">
                <span
                  aria-hidden
                  className={clsx('mt-1 size-2.5 shrink-0 rounded-full', {
                    'bg-ok': s.state === 'ok',
                    'bg-warn': s.state === 'warn',
                    'bg-danger': s.state === 'down',
                    'bg-line-strong': s.state === 'unknown',
                  })}
                />
                <div className="min-w-0">
                  <p className="flex items-center gap-2 text-sm font-medium text-fg">
                    {s.label}
                    <Pill tone={STATE_TONE[s.state]}>{s.state}</Pill>
                  </p>
                  <p className="mt-0.5 text-xs text-fg-muted">{s.detail}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section>
          <h2 className="mb-2.5 text-sm font-semibold text-fg">Response and guards</h2>
          {/*
            `/metrics` fails independently of `/health/summary`, and when it did this heading was
            followed by nothing at all — no figures, no reason, no retry. An empty section under a
            heading reads as "these numbers are all zero", which is the one thing a health page
            must never say by accident.
          */}
          {metrics.isError ? (
            <ErrorState
              error={metrics.error}
              context="load the response and guard metrics"
              onRetry={() => void metrics.refetch()}
            />
          ) : metrics.isLoading ? (
            <p className="text-sm text-fg-muted">Reading the metric definitions…</p>
          ) : (
            <div className="flex flex-wrap gap-2.5">
              {(metrics.data ?? []).map((m) => (
                <MetricTile key={m.name} metric={m} lowerIsBetter={LOWER_IS_BETTER.has(m.name)} />
              ))}
            </div>
          )}
        </section>

        <div className="grid gap-5 lg:grid-cols-3">
          <section className="h1-card p-4">
            <h2 className="text-sm font-semibold text-fg">Unanswered inbound</h2>
            {/* An em dash while it loads, not a zero. "Nobody is waiting" is a claim. */}
            <p className="mt-2 text-3xl font-semibold tabular-nums text-fg">{data ? data.unanswered.count : '—'}</p>
            <p className="mt-1 text-xs text-fg-muted">
              {data
                ? `oldest ${formatDuration(data.unanswered.maxAgeMs)} — counted live, not from a snapshot`
                : 'counted live, not from a snapshot'}
            </p>
          </section>

          <section className="h1-card p-4">
            <h2 className="text-sm font-semibold text-fg">Delivery failures (24 h)</h2>
            <ul className="mt-2 flex flex-col gap-1.5">
              {data && data.deliveryFailures.length === 0 && (
                <li className="text-xs text-fg-subtle">Nothing failed in the last 24 hours.</li>
              )}
              {(data?.deliveryFailures ?? []).map((f) => (
                <li key={f.class} className="flex items-baseline justify-between gap-2 text-xs">
                  <span className="min-w-0 truncate text-fg-muted" title={f.class}>{f.class}</span>
                  <span className={clsx('font-mono tabular-nums', f.count > 0 ? 'text-danger' : 'text-fg-subtle')}>
                    {f.count}
                  </span>
                </li>
              ))}
            </ul>
          </section>

          <section className="h1-card p-4">
            <h2 className="text-sm font-semibold text-fg">Guard fires (7 d)</h2>
            <ul className="mt-2 flex flex-col gap-1.5">
              {data && data.guardFires.length === 0 && (
                <li className="text-xs text-fg-subtle">No guard has recorded anything in seven days.</li>
              )}
              {(data?.guardFires ?? []).map((g) => (
                <li key={g.guard} className="flex items-baseline justify-between gap-2 text-xs">
                  <span className="font-mono text-fg-muted">{g.guard}</span>
                  {g.fires7d === 0 ? (
                    <Pill tone="warn" title="A guard that never fires is usually a guard that is not wired up">
                      zero fires
                    </Pill>
                  ) : (
                    <span className="font-mono tabular-nums text-fg">{g.fires7d}</span>
                  )}
                </li>
              ))}
            </ul>
          </section>
        </div>

        <div className="grid gap-5 lg:grid-cols-2">
          <section className="h1-card p-4">
            <h2 className="text-sm font-semibold text-fg">Outbox</h2>
            <p className="mt-0.5 text-[11px] text-fg-subtle">
              <code className="font-mono">delivery.outbox</code> by ledger state — what has been asked for but not
              yet confirmed by Meta.
            </p>
            <ul className="mt-2 flex flex-col gap-1.5">
              {data && data.outbox.length === 0 && <li className="text-xs text-fg-subtle">The outbox is empty.</li>}
              {(data?.outbox ?? []).map((o) => (
                <li key={o.state} className="flex items-baseline justify-between gap-2 text-xs">
                  <span className="font-mono text-fg-muted">{o.state}</span>
                  <span
                    className={clsx(
                      'font-mono tabular-nums',
                      o.state === 'failed' || o.state === 'abandoned' ? 'text-danger' : 'text-fg',
                    )}
                  >
                    {o.count}
                  </span>
                </li>
              ))}
            </ul>
          </section>

          <section className="h1-card p-4">
            <h2 className="text-sm font-semibold text-fg">Job queue</h2>
            <p className="mt-2 text-3xl font-semibold tabular-nums text-fg">
              {/* Null is "this process cannot see the queue", not "the queue is empty". */}
              {data ? (data.queue.depth === null ? '—' : data.queue.depth) : '—'}
            </p>
            <p className="mt-1 text-xs text-fg-muted">
              {data?.queue.detail ?? 'asking…'}
              {data?.queue.oldestMs !== null && data?.queue.oldestMs !== undefined
                ? ` · oldest job ${formatDuration(data.queue.oldestMs)}`
                : ''}
            </p>
          </section>
        </div>

        <section className="grid gap-5 lg:grid-cols-2">
          <div className="h1-card p-4">
            <h2 className="text-sm font-semibold text-fg">Config</h2>
            <dl className="mt-2 grid grid-cols-[8rem_1fr] gap-x-3 gap-y-1.5 text-xs">
              <dt className="text-fg-subtle">Pack digest</dt>
              <dd className="font-mono text-fg">{data?.packDigest ?? '—'}</dd>
              <dt className="text-fg-subtle">Config drift</dt>
              <dd>{data?.configDrift ? <Pill tone="danger">effective config differs from the pack</Pill> : <Pill tone="ok">in sync</Pill>}</dd>
            </dl>
          </div>
          <div className="h1-card border-dashed p-4">
            <h2 className="text-sm font-semibold text-fg">Backups</h2>
            <p className="mt-2 text-sm text-fg-muted">
              <strong className="text-fg">Nightly, and proved weekly.</strong> <code>h1-backup.timer</code> takes a
              <code> pg_dump -Fc</code> and rsyncs the media tree at 02:45; <code>h1-restore-drill.timer</code> restores
              the newest dump into a scratch database at 03:40 on Sundays and counts the rows back. An unproved backup
              is not a backup, so the drill is the one that matters.
            </p>
            <p className="mt-2 text-sm text-fg-muted">
              No timestamp here on purpose: <code>/var/backups/h1</code> is <code>root:root 0700</code> and this process
              runs as <code>h1</code>, which cannot read it — a console that could read the backups could also be used
              to find them. Check the box:
              <code className="block mt-1">systemctl list-timers &apos;h1-*&apos;</code>
              <code className="block">journalctl -u h1-restore-drill -n 30</code>
            </p>
          </div>
        </section>
      </div>
    </div>
  );
}
